"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  clampRefreshIntervalMs,
  DERIVED_SERIES_KEY,
  isFixedTimeRangeKey,
  parseStandardWidgetConfig,
  type AggregationKey,
  type DashboardTimeRangeSelection,
} from "@/components/dashboard/widget-config";
import { postTelemetryBatchQuery } from "@/lib/api/endpoints/telemetry";
import type { DashboardWidgetDto, TelemetryBatchQueryItemDto } from "@/lib/api/types";

import type { WidgetSeriesNormalized } from "./useWidgetData";
import { normalizeAggregates } from "./useWidgetData";

export type DashboardSeriesEntry = {
  data: WidgetSeriesNormalized | null;
  error: Error | null;
};

export type DashboardTelemetryBatchContextValue = {
  seriesByWidgetId: Map<string, DashboardSeriesEntry>;
  batchAggregateWidgetIds: Set<string>;
  isLoading: boolean;
  isFetching: boolean;
  /** Set when the batch request fails at the transport / HTTP layer. */
  globalQueryError: Error | null;
  error: Error | null;
  refetch: () => Promise<unknown>;
  dataUpdatedAt: number;
};

function batchFingerprint(queries: TelemetryBatchQueryItemDto[]): string {
  const sorted = [...queries].sort((a, b) => a.widget_id.localeCompare(b.widget_id));
  return JSON.stringify(sorted);
}

function minRefreshIntervalMs(widgets: DashboardWidgetDto[]): number {
  let minSec = 10;
  for (const w of widgets) {
    const raw = w.config?.refresh_interval_seconds;
    if (typeof raw === "number" && Number.isFinite(raw)) {
      minSec = Math.min(minSec, Math.max(10, Math.min(120, raw)));
    }
  }
  return clampRefreshIntervalMs(minSec);
}

export function buildTelemetryBatchQueries(
  widgets: DashboardWidgetDto[],
  globalTimeRange: DashboardTimeRangeSelection | null,
): { queries: TelemetryBatchQueryItemDto[]; batchWidgetIds: Set<string> } {
  const queries: TelemetryBatchQueryItemDto[] = [];
  const batchWidgetIds = new Set<string>();
  if (globalTimeRange?.key === "custom") {
    return { queries, batchWidgetIds };
  }
  const fixedGlobalTimeRange = isFixedTimeRangeKey(globalTimeRange?.key) ? globalTimeRange.key : null;
  for (const w of widgets) {
    if (w.type === "table") {
      continue;
    }
    const parsed = parseStandardWidgetConfig(w.config ?? {});
    if (!parsed.ok) {
      continue;
    }
    const tr = fixedGlobalTimeRange ?? parsed.data.time_range;
    const q: TelemetryBatchQueryItemDto = {
      widget_id: w.id,
      device_id: parsed.data.device_id,
      time_range: tr,
      aggregation: parsed.data.aggregation,
    };
    if (parsed.data.channel) {
      q.channel = parsed.data.channel;
    }
    if (parsed.data.formula) {
      q.formula = parsed.data.formula;
    }
    if (parsed.data.metric_id) {
      q.metric_id = parsed.data.metric_id;
    }
    queries.push(q);
    batchWidgetIds.add(w.id);
  }
  return { queries, batchWidgetIds };
}

type UseDashboardDataArgs = {
  tenantId: string;
  dashboardId: string;
  widgets: DashboardWidgetDto[];
  globalTimeRange: DashboardTimeRangeSelection | null;
};

export function useDashboardData({
  tenantId,
  dashboardId,
  widgets,
  globalTimeRange,
}: UseDashboardDataArgs): DashboardTelemetryBatchContextValue {
  const { queries, batchWidgetIds } = useMemo(
    () => buildTelemetryBatchQueries(widgets, globalTimeRange),
    [widgets, globalTimeRange],
  );

  const fingerprint = useMemo(() => batchFingerprint(queries), [queries]);
  const refetchInterval = useMemo(() => minRefreshIntervalMs(widgets), [widgets]);

  const query = useQuery({
    queryKey: ["dashboard-telemetry-batch", dashboardId, globalTimeRange ?? "", fingerprint],
    queryFn: async () => postTelemetryBatchQuery({ tenant_id: tenantId, queries }),
    enabled: Boolean(tenantId && dashboardId && queries.length > 0),
    staleTime: Math.min(refetchInterval, 60_000),
    refetchInterval,
    refetchOnWindowFocus: false,
  });

  return useMemo((): DashboardTelemetryBatchContextValue => {
    const seriesByWidgetId = new Map<string, DashboardSeriesEntry>();
    const res = query.data?.results ?? {};
    const globalQueryError =
      query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null;

    for (const q of queries) {
      const row = res[q.widget_id];
      if (!row) {
        seriesByWidgetId.set(q.widget_id, {
          data: null,
          error: globalQueryError ?? new Error("Missing batch result for widget."),
        });
        continue;
      }
      if (row.error) {
        seriesByWidgetId.set(q.widget_id, { data: null, error: new Error(row.error) });
        continue;
      }
      const seriesKey = q.channel ?? DERIVED_SERIES_KEY;
      const points = row.timestamps.map((t, i) => ({
        t,
        values: { [seriesKey]: row.values[i] ?? null } as Record<string, number | null>,
      }));
      const aggKey = q.aggregation as AggregationKey;
      const data = normalizeAggregates(
        {
          bucket_seconds: row.bucket_seconds,
          metrics: [seriesKey],
          points,
        },
        seriesKey,
        aggKey,
      );
      seriesByWidgetId.set(q.widget_id, { data, error: null });
    }

    return {
      seriesByWidgetId,
      batchAggregateWidgetIds: batchWidgetIds,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      globalQueryError,
      error: globalQueryError,
      refetch: query.refetch,
      dataUpdatedAt: query.dataUpdatedAt,
    };
  }, [
    queries,
    query.data,
    query.error,
    query.isLoading,
    query.isFetching,
    query.refetch,
    query.dataUpdatedAt,
    batchWidgetIds,
  ]);
}
